import classNames from 'classnames';
import React, { useEffect } from 'react';
import { ListingCard } from '../../../../components';
import { useIntl } from '../../../../util/reactIntl';
import { trackEvent, listingToItem } from '../../../../analytics/track';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionFeaturedListings.module.css';
import Slider from 'react-slick';

// A thin arrow pointing right; the "previous" button mirrors it.
const IconArrow = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 12h16" />
    <path d="M14 6l6 6-6 6" />
  </svg>
);

// Replaces slick's own small arrows. Slick passes its classes (including
// `slick-disabled` at either end of the list), the current slide and the click
// handler. On narrow screens slick can report the start of a list as slide -1
// and then leaves "previous" enabled, so that end is also checked directly.
const CarouselArrow = props => {
  const { className, onClick, isNext, label, currentSlide } = props;
  const isDisabled =
    className?.includes('slick-disabled') || (!isNext && currentSlide <= 0);
  return (
    <button
      type="button"
      className={classNames(css.arrow, isNext ? css.arrowNext : css.arrowPrev, {
        [css.arrowDisabled]: isDisabled,
      })}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={label}
    >
      <IconArrow />
    </button>
  );
};

// Slick slider settings
const sliderSettings = {
  dots: false,
  infinite: false,
  speed: 500,
  slidesToShow: 4,
  slidesToScroll: 1,
  variableWidth: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 3,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const cardRenderSizes = isMapVariant => {
  if (isMapVariant) {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 767px) 100vw',
      `(max-width: 1023px) ${panelMediumWidth}vw`,
      `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
      `${panelLargeWidth / 3}vw`,
    ].join(', ');
  } else {
    // Panel width relative to the viewport
    const panelMediumWidth = 50;
    const panelLargeWidth = 62.5;
    return [
      '(max-width: 549px) 100vw',
      '(max-width: 767px) 50vw',
      `(max-width: 1439px) 26vw`,
      `(max-width: 1920px) 18vw`,
      `14vw`,
    ].join(', ');
  }
};

// Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
const SectionFeaturedListings = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    isInsideContainer,
    options,
    listings,
  } = props;

  const intl = useIntl();

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasListings = listings.length > 0;

  // Curators name collections through Console, so the section title is the
  // label worth reporting on. Fall back to the section id (e.g. 'active-winter').
  const listName = title?.content || sectionId;
  const listParams = { item_list_id: sectionId, item_list_name: listName };

  useEffect(() => {
    if (hasListings) {
      trackEvent('view_item_list', {
        ...listParams,
        items: listings.map((listing, index) => listingToItem(listing, { index })),
      });
    }
  }, [sectionId, listings.length]);

  // react-slick can measure a carousel mid-update (e.g. while it swaps to a
  // responsive breakpoint after hydration on tablet widths) and leave the track
  // without a width, which squashes every slide. A resize makes it re-measure.
  useEffect(() => {
    const timer = setTimeout(() => window.dispatchEvent(new Event('resize')), 300);
    return () => clearTimeout(timer);
  }, [listings.length]);

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
        </header>
      ) : null}

      <section className={css.listingCards}>
        <Slider
          {...sliderSettings}
          prevArrow={
            <CarouselArrow
              label={intl.formatMessage({ id: 'SectionFeaturedListings.previous' })}
            />
          }
          nextArrow={
            <CarouselArrow
              isNext
              label={intl.formatMessage({ id: 'SectionFeaturedListings.next' })}
            />
          }
        >
          {listings.map((listing, index) => (
            <div
              key={listing.id.uuid}
              style={{ width: '290px', paddingRight: '16px' }}
              className={css.listingCardContainer}
              onClick={() =>
                trackEvent('select_item', {
                  ...listParams,
                  items: [listingToItem(listing, { index })],
                })
              }
            >
              <ListingCard
                className={css.listingCard}
                listing={listing}
                renderSizes={cardRenderSizes(true)}
              />
            </div>
          ))}
        </Slider>
      </section>
    </SectionContainer>
  );
};

export default SectionFeaturedListings;
